// Config → Backgrounds
// Preview every registered background and pick one per major menu section.
// The rear-most `body::before` layer swaps as you navigate.
import { Palette, RotateCcw, Check, Layers } from "lucide-react";
import Layout from "../../components/Layout";
import { useBackground } from "../../context/BackgroundContext";
import { BASE_COLOR, BACKGROUNDS_BY_ID } from "../../config/backgrounds";

function Swatch({ bg, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`bg-swatch-${bg.id}`}
      className={`relative group text-left rounded-xl overflow-hidden border transition-all ${
        selected
          ? "border-teal-400/80 shadow-[0_0_0_2px_rgba(45,212,191,0.35)]"
          : "border-white/10 hover:border-white/25"
      }`}
      style={{ aspectRatio: "16 / 10" }}
    >
      <span
        className="absolute inset-0"
        style={{ backgroundColor: BASE_COLOR, backgroundImage: bg.css }}
        aria-hidden="true"
      />
      <span className="absolute inset-x-0 bottom-0 px-3 py-2 bg-black/55 backdrop-blur-sm">
        <span className="block text-[12.5px] font-semibold text-white truncate">{bg.label}</span>
        <span className="block text-[9.5px] font-mono uppercase tracking-widest text-slate-300 truncate">{bg.tone}</span>
      </span>
      {selected && (
        <span className="absolute top-2 right-2 w-6 h-6 grid place-items-center rounded-full bg-teal-400 text-slate-900">
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

/**
 * PresetCard — a 9-tile mini-mosaic showing every section-tile a preset paints,
 * with the preset label, accent, and tagline underneath.
 */
function PresetCard({ preset, selected, onClick }) {
  // Draw the 9 section tiles as a 3×3 mosaic using each background's CSS.
  const tiles = Object.entries(preset.map).slice(0, 9);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`bg-preset-${preset.id}`}
      className={`relative text-left rounded-2xl overflow-hidden border transition-all p-3 flex flex-col gap-2.5 ${
        selected
          ? "border-teal-400/80 shadow-[0_0_0_2px_rgba(45,212,191,0.35)] bg-white/[0.04]"
          : "border-white/10 hover:border-white/25 bg-white/[0.02]"
      }`}
    >
      {/* mosaic */}
      <div className="grid grid-cols-3 gap-1 rounded-lg overflow-hidden" style={{ aspectRatio: "16 / 10", backgroundColor: BASE_COLOR }}>
        {tiles.map(([sec, bgId]) => {
          const bg = BACKGROUNDS_BY_ID[bgId];
          return (
            <span
              key={sec}
              className="block"
              style={{
                backgroundColor: BASE_COLOR,
                backgroundImage: bg?.css || "",
              }}
              aria-hidden="true"
              title={sec}
            />
          );
        })}
      </div>

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-white truncate">{preset.label}</div>
          <div className="text-[10.5px] text-slate-400 line-clamp-2 mt-0.5">{preset.tagline}</div>
        </div>
        <span
          className="mt-0.5 w-4 h-4 rounded-full shrink-0 border border-white/15"
          style={{ background: preset.accent }}
          aria-hidden="true"
        />
      </div>

      {selected && (
        <span className="absolute top-2 right-2 w-6 h-6 grid place-items-center rounded-full bg-teal-400 text-slate-900 z-10">
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

export default function BackgroundsSettings() {
  const {
    map, sections, backgrounds, setSectionBg, resetDefaults,
    presets, activePresetId, applyPreset,
  } = useBackground();

  return (
    <Layout>
      <div className="space-y-8 max-w-6xl mx-auto" data-testid="settings-backgrounds">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-teal-300 font-mono mb-1 flex items-center gap-1.5">
              <Palette className="w-3 h-3" /> Config · Backgrounds
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight">Backgrounds</h1>
            <p className="text-slate-400 text-sm mt-1 max-w-2xl">
              Pick a rear-most background layer for each major section. The chosen
              background swaps automatically as you navigate — subtle visual signal
              so you always know where you are.
            </p>
          </div>
          <button
            type="button"
            onClick={resetDefaults}
            data-testid="bg-reset-defaults"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-slate-300 hover:text-white hover:bg-white/[0.06]"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
          </button>
        </div>

        {/* Theme presets — one-click paint all 9 sections */}
        <section data-testid="bg-presets">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-teal-300" /> Theme Presets
              </h2>
              <p className="text-[11.5px] text-slate-400 mt-0.5">
                One click to paint every section with a coordinated palette. Overrides the per-section picks below.
              </p>
            </div>
            <span className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">
              Active: <span className="text-teal-300">
                {presets.find((p) => p.id === activePresetId)?.label || "Custom / per-section"}
              </span>
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {presets.map((preset) => (
              <PresetCard
                key={preset.id}
                preset={preset}
                selected={preset.id === activePresetId}
                onClick={() => applyPreset(preset.id)}
              />
            ))}
          </div>
        </section>

        {/* One row per section — fine-grained picks */}
        <div className="space-y-8">
          <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500 -mb-4">
            Per-section overrides
          </div>
          {sections.map((s) => {
            const selected = map[s.key];
            return (
              <section key={s.key} data-testid={`bg-section-${s.key}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <h2 className="text-lg font-semibold text-white">{s.label}</h2>
                  <span className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">
                    Selected: <span className="text-teal-300">{backgrounds.find((b) => b.id === selected)?.label || "—"}</span>
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {backgrounds.map((bg) => (
                    <Swatch
                      key={bg.id}
                      bg={bg}
                      selected={selected === bg.id}
                      onClick={() => setSectionBg(s.key, bg.id)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <div className="text-[10.5px] text-slate-600 font-mono">
          Settings saved to <code className="text-slate-500">localStorage</code> (per-browser).
          Wire to user preferences in DB later.
        </div>
      </div>
    </Layout>
  );
}
